import {
	Button,
	ButtonGroup,
	IconButton,
	LuIcon,
	type MenuVirtualTriggerProps,
	radiusCva,
	shadowCva,
	Text,
	ToggleGroup,
	useLayoutContext,
	useProvider,
} from "@overmap-ai/blocks"
import { type Asset, type AssetType, OrganizationAccessLevel } from "@overmap-ai/common"
import { selectAssetsMapping, selectAssetTypesMapping, selectProjectById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import type { Polygon, Position } from "geojson"
import { SquareDashed, SquircleDashed } from "lucide-react"
import { memo, useCallback, useMemo, useState } from "react"
import { useMap } from "react-map-gl/mapbox"

import { BottomBar } from "../../components/BottomBar/BottomBar"
import { BulkAssetControl } from "../../components/BulkAssetControl/BulkAssetControl"
import { ObjectCreationMenu } from "../../components/DropdownMenus/ObjectCreationMenu"
import { MapContextMenu } from "../../components/MapContextMenus/MapContextMenu"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ProjectFileVisibilityPopover } from "../../components/Popovers/ProjectFileVisibilityPopover"
import { useSelectedAssets } from "../../contexts/hooks"
import { ringCva } from "../../cva"
import { useGeolocation } from "../../hooks/utils"
import { PolygonDrawer, RectangleDrawer } from "../../mapbox/components"
import { BOUNDS_PADDING } from "../../mapbox/constants"
import { useCurrentMapEvent } from "../../mapbox/hooks"
import { getAssetTypeLayerIds } from "../../mapbox/layers/utils"
import { CurrentLocationMarker } from "../../mapbox/markers"
import type { MapEventHandler } from "../../mapbox/typings"
import { flattenGeometry, polygonToLngLatBounds } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { appStore, useAppSelector } from "../../store"

enum MapViewMapMode {
	MULTI_SELECT_RECTANGLE = "multi-select-rectangle",
	MULTI_SELECT_POLYGON = "multi-select-polygon",
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId")

export const MapView = memo(() => {
	const { projectId } = routeApi.useParams()

	const project = useAppSelector(selectProjectById(projectId))

	if (!project) {
		throw new Error(`Expected project to be defined, got ${project}`)
	}

	const { setSelectedAssetIds, selectedAssetIds } = useSelectedAssets()
	const { current: map } = useMap()
	const assetsMapping = useAppSelector(selectAssetsMapping)
	const { hideLayout } = useLayoutContext()
	const userCoordinates = useGeolocation()
	const { radius } = useProvider()
	const [mapViewMode, setMapViewMode] = useState<MapViewMapMode | "">("")

	const [mapPointVirtualElement, setMapPointVirtualElement] =
		useState<MenuVirtualTriggerProps["virtualElement"]>(null)
	const [coordinates, setCoordinates] = useState<Position | null>(null)

	const selectedAssetsByAssetType = useMemo(() => {
		const ret: Record<AssetType["uuid"], Asset["uuid"][]> = {}

		for (const assetId of selectedAssetIds) {
			const asset = assetsMapping[assetId]

			if (!asset) continue

			let newEncompassedAssetsByThisType = ret[asset.asset_type]!

			if (!newEncompassedAssetsByThisType) newEncompassedAssetsByThisType = []

			newEncompassedAssetsByThisType.push(assetId)
			ret[asset.asset_type] = newEncompassedAssetsByThisType
		}

		return ret
	}, [assetsMapping, selectedAssetIds])

	const clearAssetSelection = useCallback(() => {
		setSelectedAssetIds([])
		setMapViewMode("")
	}, [setSelectedAssetIds])

	const handleMapContextMenu = useCallback<MapEventHandler<"contextmenu">>((e) => {
		const { originalEvent, lngLat } = e

		if (e.defaultPrevented) return

		setCoordinates(lngLat.toArray())
		setMapPointVirtualElement({
			getBoundingClientRect() {
				return {
					height: 0,
					width: 0,
					x: 0,
					y: 0,
					left: originalEvent.clientX,
					top: originalEvent.clientY,
					bottom: 0,
					right: 0,
				}
			},
		})
	}, [])

	useCurrentMapEvent("contextmenu", handleMapContextMenu)

	const handleAssetMultiSelect = useCallback(
		(polygon: Polygon) => {
			if (!map) return

			const assetTypeIds = Object.keys(selectAssetTypesMapping(appStore.getState()))

			const layersToQuery = assetTypeIds.flatMap((assetTypeId) => {
				const { assetCircleLayerId, assetLineLayerId, assetFillLayerId } = getAssetTypeLayerIds(assetTypeId)
				return [assetCircleLayerId, assetLineLayerId, assetFillLayerId]
			})

			const features = map.queryRenderedFeatures({
				layers: layersToQuery,
			})

			// NOTE: Mapbox layers may split complex geometries: MultiPoint, MultiLineString, MultiPolygon, GeometryCollection
			// even if they are passed within a single feature to the source of a given layer, so there could be multiple
			// features that have the same uuid in the return from the queryRenderedFeatures function
			const selectAssetIdsSet = new Set<Asset["uuid"]>()

			for (const feature of features) {
				const uuid = feature.properties!.uuid as Asset["uuid"]

				const within = flattenGeometry(feature.geometry).every((geometry) => {
					return turf.booleanWithin(geometry, polygon)
				})

				if (within) {
					selectAssetIdsSet.add(uuid)
				} else {
					selectAssetIdsSet.delete(uuid)
				}
			}

			setSelectedAssetIds(Array.from(selectAssetIdsSet.values()))
			setMapViewMode("")
		},
		[setSelectedAssetIds, map],
	)

	const handleLocateProjectClick = useCallback(() => {
		if (!map || !project.bounds) return

		map.fitBounds(polygonToLngLatBounds(project.bounds), {
			animate: true,
			padding: BOUNDS_PADDING,
		})
	}, [map, project.bounds])

	const handleLocateUser = useCallback(() => {
		if (!userCoordinates.latitude || !userCoordinates.longitude) return

		map?.flyTo({
			center: [userCoordinates.longitude, userCoordinates.latitude],
			zoom: 15,
			animate: true,
			padding: BOUNDS_PADDING,
		})
	}, [map, userCoordinates.latitude, userCoordinates.longitude])

	return (
		<>
			<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
				{coordinates && (
					<MapContextMenu
						// TODO: control open state and close when zooming in and out
						modal
						offset={20}
						virtualElement={mapPointVirtualElement}
						coordinates={coordinates}
					/>
				)}
			</OrganizationAccessRequired>

			<CurrentLocationMarker />

			{mapViewMode === MapViewMapMode.MULTI_SELECT_POLYGON && (
				<>
					<PolygonDrawer onComplete={handleAssetMultiSelect} />
					<BottomBar className="flex flex-col gap-2">
						<Text size="sm" accentColor="base">
							Click to add points. Click the first point to close the shape.
						</Text>
						<Button variant="soft" accentColor="base" size="sm" onClick={clearAssetSelection}>
							<LuIcon icon={modelIconMapping.close} /> Cancel
						</Button>
					</BottomBar>
				</>
			)}
			{mapViewMode === MapViewMapMode.MULTI_SELECT_RECTANGLE && (
				<>
					<RectangleDrawer onComplete={handleAssetMultiSelect} />
					<BottomBar className="flex flex-col gap-2">
						<Text size="sm" accentColor="base">
							Click to set the first corner, then click again to complete the selection.
						</Text>
						<Button variant="soft" accentColor="base" size="sm" onClick={clearAssetSelection}>
							<LuIcon icon={modelIconMapping.close} /> Cancel
						</Button>
					</BottomBar>
				</>
			)}

			{!hideLayout && (
				<ButtonGroup
					className="absolute right-4 top-1/2 flex -translate-y-1/2 flex-col gap-2"
					accentColor="base"
					size="md"
					variant="soft"
				>
					<ToggleGroup.Root
						type="single"
						value={mapViewMode}
						// @ts-expect-error REASON: MapViewMode is a string enum
						onValueChange={setMapViewMode}
						variant="soft"
						size="md"
						accentColor="base"
						className={cx(
							"bg-(--color-background) flex flex-col",
							ringCva({ alpha: false }),
							radiusCva({ radius }),
							shadowCva({ size: "md" }),
						)}
					>
						<ToggleGroup.IconItem className="rounded-b-none" value={MapViewMapMode.MULTI_SELECT_RECTANGLE}>
							<LuIcon icon={SquareDashed} />
						</ToggleGroup.IconItem>

						<ToggleGroup.IconItem className="rounded-t-none" value={MapViewMapMode.MULTI_SELECT_POLYGON}>
							<LuIcon icon={SquircleDashed} />
						</ToggleGroup.IconItem>
					</ToggleGroup.Root>

					<div
						className={cx(
							"bg-(--color-background) flex flex-col",
							ringCva({ alpha: false }),
							radiusCva({ radius }),
							shadowCva({ size: "md" }),
						)}
					>
						<IconButton className="rounded-b-none" onClick={handleLocateProjectClick}>
							<LuIcon icon={modelIconMapping.map} />
						</IconButton>

						<IconButton onClick={handleLocateUser} className="rounded-none">
							<LuIcon icon={modelIconMapping.navigate} />
						</IconButton>

						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
							<ObjectCreationMenu side="left" size="sm">
								<IconButton
									className="rounded-t-none"
									accentColor="primary"
									aria-label="add"
									variant="soft"
								>
									<LuIcon icon={modelIconMapping.add} />
								</IconButton>
							</ObjectCreationMenu>
						</OrganizationAccessRequired>
					</div>
				</ButtonGroup>
			)}

			<ProjectFileVisibilityPopover side="top" align="end" size="sm">
				<IconButton
					className={cx("bg-(--color-background)! absolute bottom-4 right-4", shadowCva({ size: "md" }))}
					variant="surface"
					size="xl"
				>
					<LuIcon icon={modelIconMapping.overlay} />
				</IconButton>
			</ProjectFileVisibilityPopover>

			<BulkAssetControl
				open={!!Object.keys(selectedAssetsByAssetType).length}
				onOpenChange={clearAssetSelection}
				selectedAssetsByAssetType={selectedAssetsByAssetType}
				size="sm"
			/>
		</>
	)
})
MapView.displayName = "MapView"
